/**
 * Visibility-triggered signed-token refresh throttle.
 *
 * `useSignedTokenSync` refreshes the signed entitlement token (a licensing
 * server round-trip) on several triggers: user change, subscription-status
 * change, SIGNED_IN, TOKEN_REFRESHED, and app visibility change. The
 * visibility trigger fires on every `visibilitychange -> visible` event
 * (e.g. every time the user alt-tabs back into the app), which — unlike the
 * other triggers — is not bounded to a meaningful state change. That made it
 * the dominant source of redundant server calls (and, via the trial sync
 * side effect, redundant 'auth:trial_start_server' analytics events).
 *
 * The visibility path exists to catch the user returning from Stripe
 * checkout, which takes at least tens of seconds to minutes — so throttling
 * it to at most once per 10 minutes is safe and does not affect that use
 * case. The other triggers stay immediate; they either represent a real
 * state change (user/subscription) or are already server/main-process
 * driven at a sane cadence (SIGNED_IN, TOKEN_REFRESHED).
 */

/** Minimum time between visibility-triggered refreshes. */
export const VISIBILITY_REFRESH_THROTTLE_MS = 10 * 60 * 1000;

/**
 * Whether a visibility-triggered refresh should be skipped because the last
 * COMPLETED refresh (of any trigger) happened too recently.
 *
 * @param lastCompletedRefreshAt - epoch ms of the last completed refresh, or
 *   `null` if no refresh has completed yet (never throttled).
 * @param now - current epoch ms (injectable for testing).
 * @param throttleMs - minimum spacing between visibility-triggered refreshes.
 */
export function shouldThrottleVisibilityRefresh(
  lastCompletedRefreshAt: number | null,
  now: number = Date.now(),
  throttleMs: number = VISIBILITY_REFRESH_THROTTLE_MS
): boolean {
  if (lastCompletedRefreshAt === null) return false;
  return now - lastCompletedRefreshAt < throttleMs;
}
